// src/page/admissions.jsx
import React, { useMemo, useState } from "react";
import emailjs from "@emailjs/browser";
import "./Admissions.css";

const steps = [
  {
    number: 1,
    title: "Enquiry Form",
    description:
      "Complete our initial enquiry form to express your interest in admission. This helps us understand your requirements and provide you with relevant information.",
  },
  {
    number: 2,
    title: "Interaction & Entrance Assessment",
    description:
      "After the initial enquiry, we schedule an interaction session and entrance assessment to evaluate the student’s aptitude, skills, and knowledge. This helps us understand the student's potential and academic standing.",
  },
  {
    number: 3,
    title: "Issue of Application Form",
    description:
      "After confirmation of eligibility, parents receive the formal application form that needs to be filled with comprehensive student and parent details.",
  },
  {
    number: 4,
    title: "Admission Formalities",
    description:
      "The final step involves completing all Admission formalities to ensure a smooth integration of the student into the school system.",
  },
];

const ageCriteria = [
  { className: "Nursery", age: "3+ years" },
  { className: "LKG", age: "4+ years" },
  { className: "UKG", age: "5+ years" },
  { className: "Grade 1", age: "6+ years" },
];

const Admissions = () => {
  const [activeStep, setActiveStep] = useState(1);

  const activeStepData = useMemo(
    () => steps.find((step) => step.number === activeStep),
    [activeStep]
  );

  // ★ Step 1: Enquiry Form State
  const [enquiry, setEnquiry] = useState({
    // Student name
    firstName: "",
    middleName: "",
    lastName: "",

    gender: "",
    dob: "",

    religion: "",
    community: "",
    aadhar: "",
    nationality: "",

    gradeApplyingFor: "",
    currentClass: "",
    currentSchool: "",

    previousSchoolBoard: "",

    schoolTransportRequired: "",

    secondLanguagePreference: "",
    thirdLanguagePreference: "",

    fatherName: "",
    fatherContact: "",
    fatherProfession: "",
    fatherEmail: "",

    motherName: "",
    motherContact: "",
    motherProfession: "",
    motherEmail: "",

    medicalHistory: "",

    expectation1: "",
    expectation2: "",

    residentialAddress: "",
    correspondenceAddress: "",

    heardFrom: "",
  });

  // ★ Application Form State
  const [application, setApplication] = useState({
    studentName: "",
    dateOfBirth: "",
    classApplyingFor: "",
    aadharNumber: "",
    nationality: "",
    motherTongue: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnquirySubmitting, setIsEnquirySubmitting] = useState(false);

  const handleApplicationChange = (e) => {
    const { name, value } = e.target;
    setApplication((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.send(
        "service_6xgva7r", // Service ID
        "template_zujwj1i", // Template ID
        application, // Form data
        "uymgOEThqyWnQ1j2g" // Public Key
      );

      alert("Application submitted successfully! We will contact you soon.");
      setApplication({
        studentName: "",
        dateOfBirth: "",
        classApplyingFor: "",
        aadharNumber: "",
        nationality: "",
        motherTongue: "",
      });
    } catch (error) {
      console.error("EmailJS Error (Application):", error);
      alert("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnquiryChange = (e) => {
    const { name, value, type } = e.target;

    setEnquiry((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? e.target.checked : value,
    }));
  };

  // ✅ UPDATED: Enquiry submit now sends email via EmailJS
  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setIsEnquirySubmitting(true);

    try {
      await emailjs.send(
        "service_6xgva7r", // Service ID
        "template_64lq45l", // Template ID (Enquiry)
        enquiry, // Enquiry data
        "uymgOEThqyWnQ1j2g" // Public Key
      );

      alert("Thank you! Your enquiry has been submitted.");

      // Reset enquiry form after successful submit
      setEnquiry({
        firstName: "",
        middleName: "",
        lastName: "",

        gender: "",
        dob: "",

        religion: "",
        community: "",
        aadhar: "",
        nationality: "",

        gradeApplyingFor: "",
        currentClass: "",
        currentSchool: "",

        previousSchoolBoard: "",

        schoolTransportRequired: "",

        secondLanguagePreference: "",
        thirdLanguagePreference: "",

        fatherName: "",
        fatherContact: "",
        fatherProfession: "",
        fatherEmail: "",

        motherName: "",
        motherContact: "",
        motherProfession: "",
        motherEmail: "",

        medicalHistory: "",

        expectation1: "",
        expectation2: "",

        residentialAddress: "",
        correspondenceAddress: "",

        heardFrom: "",
      });
    } catch (error) {
      console.error("EmailJS Error (Enquiry):", error);
      alert("Failed to submit enquiry. Please try again.");
    } finally {
      setIsEnquirySubmitting(false);
    }
  };

  const renderStep1EnquiryForm = () => (
    <div className="step-detail-card">
      <h3 className="step-detail-title">{activeStepData.title}</h3>
      <p className="step-detail-text">{activeStepData.description}</p>

      <form className="enquiry-form" onSubmit={handleEnquirySubmit}>
        {/* Student Name */}
        <h4 className="step-detail-subtitle">Student Details</h4>

        <div className="form-grid">
          <input
            type="text"
            name="firstName"
            placeholder="First Name*"
            value={enquiry.firstName}
            onChange={handleEnquiryChange}
            required
          />
          <input
            type="text"
            name="middleName"
            placeholder="Middle Name"
            value={enquiry.middleName}
            onChange={handleEnquiryChange}
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name*"
            value={enquiry.lastName}
            onChange={handleEnquiryChange}
            required
          />

          {/* Gender */}
          <div className="form-field full">
            <label className="form-label">Gender*</label>
            <div className="radio-row">
              <label className="radio-item">
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={enquiry.gender === "Male"}
                  onChange={handleEnquiryChange}
                  required
                />
                Male
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={enquiry.gender === "Female"}
                  onChange={handleEnquiryChange}
                />
                Female
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="gender"
                  value="Others"
                  checked={enquiry.gender === "Others"}
                  onChange={handleEnquiryChange}
                />
                Others
              </label>
            </div>
          </div>

          {/* DOB */}
          <input
            type="date"
            name="dob"
            value={enquiry.dob}
            onChange={handleEnquiryChange}
            required
          />

          {/* Religion */}
          <div className="form-field full">
            <label className="form-label">Religion*</label>
            <div className="radio-row">
              {["Hindu", "Muslim", "Christian", "Others"].map((r) => (
                <label className="radio-item" key={r}>
                  <input
                    type="radio"
                    name="religion"
                    value={r}
                    checked={enquiry.religion === r}
                    onChange={handleEnquiryChange}
                    required
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>

          {/* Community */}
          <div className="form-field">
            <label className="form-label">Community*</label>
            <select
              name="community"
              value={enquiry.community}
              onChange={handleEnquiryChange}
              required
            >
              <option value="">Select Community</option>
              <option value="SC">SC</option>
              <option value="ST">ST</option>
              <option value="OBC">OBC</option>
              <option value="Others">Others</option>
            </select>
          </div>

          {/* Aadhaar */}
          <input
            type="text"
            name="aadhar"
            placeholder="Aadhaar No."
            value={enquiry.aadhar}
            onChange={handleEnquiryChange}
            inputMode="numeric"
            maxLength={12}
          />

          {/* Nationality */}
          <input
            type="text"
            name="nationality"
            placeholder="Nationality*"
            value={enquiry.nationality}
            onChange={handleEnquiryChange}
            required
          />

          {/* Class / School */}
          <input
            type="text"
            name="gradeApplyingFor"
            placeholder="Grade Applying For*"
            value={enquiry.gradeApplyingFor}
            onChange={handleEnquiryChange}
            required
          />
          <input
            type="text"
            name="currentClass"
            placeholder="Current Class"
            value={enquiry.currentClass}
            onChange={handleEnquiryChange}
          />
          <input
            type="text"
            name="currentSchool"
            placeholder="Current School"
            value={enquiry.currentSchool}
            onChange={handleEnquiryChange}
          />

          {/* Previous School Board */}
          <div className="form-field full">
            <label className="form-label">Previous School Board</label>
            <div className="radio-row">
              {["SSC", "CBSE", "ICSE", "Others"].map((b) => (
                <label className="radio-item" key={b}>
                  <input
                    type="radio"
                    name="previousSchoolBoard"
                    value={b}
                    checked={enquiry.previousSchoolBoard === b}
                    onChange={handleEnquiryChange}
                  />
                  {b}
                </label>
              ))}
            </div>
          </div>

          {/* Transport */}
          <div className="form-field full">
            <label className="form-label">School Transport Required</label>
            <div className="radio-row">
              <label className="radio-item">
                <input
                  type="radio"
                  name="schoolTransportRequired"
                  value="Yes"
                  checked={enquiry.schoolTransportRequired === "Yes"}
                  onChange={handleEnquiryChange}
                />
                Yes
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="schoolTransportRequired"
                  value="No"
                  checked={enquiry.schoolTransportRequired === "No"}
                  onChange={handleEnquiryChange}
                />
                No
              </label>
            </div>
          </div>

          {/* Languages */}
          <div className="form-field">
            <label className="form-label">Second Language Preference</label>
            <select
              name="secondLanguagePreference"
              value={enquiry.secondLanguagePreference}
              onChange={handleEnquiryChange}
            >
              <option value="">Select</option>
              <option value="Telugu">Telugu</option>
              <option value="Hindi">Hindi</option>
              <option value="Sanskrit">Sanskrit</option>
            </select>
          </div>

          <div className="form-field">
            <label className="form-label">Third Language</label>
            <select
              name="thirdLanguagePreference"
              value={enquiry.thirdLanguagePreference}
              onChange={handleEnquiryChange}
            >
              <option value="">Select</option>
              {/* <option value="Telugu">Telugu</option> */}
              
              <option value="Marathi">Marathi</option>
            </select>
          </div>
        </div>

        {/* Parents */}
        <h4 className="step-detail-subtitle">Parent Details</h4>

        <div className="form-grid">
          <input
            type="text"
            name="fatherName"
            placeholder="Father Name"
            value={enquiry.fatherName}
            onChange={handleEnquiryChange}
          />
          <input
            type="text"
            name="fatherContact"
            placeholder="Father Contact No."
            value={enquiry.fatherContact}
            onChange={handleEnquiryChange}
            inputMode="tel"
          />
          <input
            type="text"
            name="fatherProfession"
            placeholder="Father Profession"
            value={enquiry.fatherProfession}
            onChange={handleEnquiryChange}
          />
          <input
            type="email"
            name="fatherEmail"
            placeholder="Father Email ID"
            value={enquiry.fatherEmail}
            onChange={handleEnquiryChange}
          />

          <input
            type="text"
            name="motherName"
            placeholder="Mother Name"
            value={enquiry.motherName}
            onChange={handleEnquiryChange}
          />
          <input
            type="text"
            name="motherContact"
            placeholder="Mother Contact No."
            value={enquiry.motherContact}
            onChange={handleEnquiryChange}
            inputMode="tel"
          />
          <input
            type="text"
            name="motherProfession"
            placeholder="Mother Profession"
            value={enquiry.motherProfession}
            onChange={handleEnquiryChange}
          />
          <input
            type="email"
            name="motherEmail"
            placeholder="Mother Email ID"
            value={enquiry.motherEmail}
            onChange={handleEnquiryChange}
          />
        </div>

        {/* Medical */}
        <div className="form-field full" style={{ marginTop: 12 }}>
          <label className="form-label">Medical history of the ward (if any)</label>
          <textarea
            name="medicalHistory"
            placeholder="Type here..."
            value={enquiry.medicalHistory}
            onChange={handleEnquiryChange}
            rows={3}
          />
        </div>

        {/* Expectations */}
        <div className="form-field full" style={{ marginTop: 12 }}>
          <label className="form-label">Your expectation from the organization</label>
          <input
            type="text"
            name="expectation1"
            placeholder="1)"
            value={enquiry.expectation1}
            onChange={handleEnquiryChange}
          />
          <input
            type="text"
            name="expectation2"
            placeholder="2)"
            value={enquiry.expectation2}
            onChange={handleEnquiryChange}
            style={{ marginTop: 8 }}
          />
        </div>

        {/* Addresses */}
        <div className="form-grid" style={{ marginTop: 12 }}>
          <div className="form-field">
            <label className="form-label">Residential Address</label>
            <textarea
              name="residentialAddress"
              placeholder="Residential Address"
              value={enquiry.residentialAddress}
              onChange={handleEnquiryChange}
              rows={3}
            />
          </div>

          <div className="form-field">
            <label className="form-label">Correspondence Address</label>
            <textarea
              name="correspondenceAddress"
              placeholder="Correspondence Address"
              value={enquiry.correspondenceAddress}
              onChange={handleEnquiryChange}
              rows={3}
            />
          </div>
        </div>

        {/* Heard From */}
        <div className="form-field full" style={{ marginTop: 12 }}>
          <label className="form-label">
            Where did you hear about Vivek Vardhini Creative Minds School?
          </label>
          <div className="radio-row">
            {[
              "Hoarding",
              "Pamphlet",
              "News Paper Ad",
              "Website",
              "Reference",
              "Others",
            ].map((src) => (
              <label className="radio-item" key={src}>
                <input
                  type="radio"
                  name="heardFrom"
                  value={src}
                  checked={enquiry.heardFrom === src}
                  onChange={handleEnquiryChange}
                />
                {src}
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="submit-btn"
          style={{ marginTop: 16 }}
          disabled={isEnquirySubmitting}
        >
          {isEnquirySubmitting ? "Submitting..." : "Submit Enquiry"}
        </button>
      </form>
    </div>
  );

  const renderInteractionAssessment = () => (
    <div className="step-detail-card">
      <h3 className="step-detail-title">{activeStepData.title}</h3>
      <p className="step-detail-text">{activeStepData.description}</p>

      <div className="interview-grid">
        <div className="interview-box">
          <h4 className="interview-box-title">Assessment Details</h4>
          <p className="interview-box-text">
            <strong>Duration:</strong> 1 hour <br />
            <br />
            <strong>Subjects:</strong> English, Mathematics, General Knowledge{" "}
            <br />
            <br />
            <strong>Format:</strong> Written & Oral
          </p>
        </div>

        <div className="interview-box">
          <h4 className="interview-box-title">Preparation Tips</h4>
          <ul className="interview-list">
            <li>Review grade-appropriate concepts</li>
            <li>Practice reading comprehension</li>
            <li>Revise basic mathematical operations</li>
            <li>Stay calm and confident</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderIssueApplicationForm = () => (
    <div className="step-detail-card">
      <h3 className="step-detail-title">{activeStepData.title}</h3>
      <p className="step-detail-text">{activeStepData.description}</p>

      <div className="interview-grid">
        <div className="interview-box">
          <h4 className="interview-box-title">Form Details</h4>
          <p className="interview-box-text">
            The application form requires detailed information about the student
            and parents, including academic history, medical information,
            emergency contacts, and transportation requirements.
          </p>
        </div>
      </div>
    </div>
  );

  const renderJoiningFormalities = () => (
    <div className="step-detail-card">
      <h3 className="step-detail-title">{activeStepData.title}</h3>
      <p className="step-detail-text">{activeStepData.description}</p>

      <div className="interview-grid">
        <div className="interview-box">
          <h4 className="interview-box-title">Orientation Program</h4>
          <p className="interview-box-text">
            Parents and students attend an orientation introducing the school’s
            ethos, faculty, facilities, and academic expectations.
          </p>
        </div>

        <div className="interview-box">
          <h4 className="interview-box-title">Final Checklist</h4>
          <ul className="interview-list">
            <li>Signed school rules</li>
            <li>Medical fitness certificate</li>
            <li>Transport form (if applicable)</li>
            <li>Book & uniform purchase</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admission-wrapper">
      <h2 className="admission-title">Admission Process</h2>
      <p className="admission-subtitle">
        Follow the steps below to understand our structured admission process.
      </p>

      <div className="steps-container">
        {steps.map((step) => (
          <div
            className="step-item"
            key={step.number}
            onClick={() => setActiveStep(step.number)}
          >
            <div
              className={`step-circle ${
                activeStep === step.number ? "active" : ""
              }`}
            >
              {step.number}
            </div>
            <p className="step-text">{step.title}</p>
          </div>
        ))}
      </div>

      {activeStep === 1 && renderStep1EnquiryForm()}
      {activeStep === 2 && renderInteractionAssessment()}
      {activeStep === 3 && renderIssueApplicationForm()}
      {activeStep === 4 && renderJoiningFormalities()}

      {/* AGE CRITERIA SECTION */}
      <section className="age-criteria-section">
        <h2 className="age-title">Age Criteria</h2>
        <p className="age-subtitle">
          Admissions are based on the child&apos;s age as on 31st March of the
          academic year. The indicative age requirements for each class are
          given below.
        </p>

        <div className="age-grid">
          {ageCriteria.map((item, index) => (
            <div className="age-card" key={index}>
              <div className="age-class">{item.className}</div>
              <div className="age-value">{item.age}</div>
              <p className="age-note">Age on or before 31st March</p>
            </div>
          ))}
        </div>
      </section>

      {/* ★★★★★ ADMISSION FORM START ★★★★★ */}
      <section className="form-section">
        <h2 className="form-title">Application Form</h2>
        <p className="form-subtitle">
          Kindly fill in the details below to apply for admission at VVCMS
          School.
        </p>

        <form className="admission-form" onSubmit={handleApplicationSubmit}>
          <h3 className="form-group-heading">Student Details</h3>

          <div className="form-grid">
            <input
              type="text"
              name="studentName"
              placeholder="Full Name of Student*"
              value={application.studentName}
              onChange={handleApplicationChange}
              required
            />
            <input
              type="date"
              name="dateOfBirth"
              placeholder="Date of Birth*"
              value={application.dateOfBirth}
              onChange={handleApplicationChange}
              required
            />
            <select
              name="classApplyingFor"
              value={application.classApplyingFor}
              onChange={handleApplicationChange}
              required
            >
              <option value="">Select Class Applying For*</option>
              <option value="Nursery">Nursery</option>
              <option value="LKG">LKG</option>
              <option value="UKG">UKG</option>
              <option value="Grade 1">Grade 1</option>
            </select>
            <input
              type="text"
              name="aadharNumber"
              placeholder="Aadhar Number"
              value={application.aadharNumber}
              onChange={handleApplicationChange}
            />
            <input
              type="text"
              name="nationality"
              placeholder="Nationality*"
              value={application.nationality}
              onChange={handleApplicationChange}
              required
            />
            <input
              type="text"
              name="motherTongue"
              placeholder="Mother Tongue"
              value={application.motherTongue}
              onChange={handleApplicationChange}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </section>
      {/* ★★★★★ ADMISSION FORM END ★★★★★ */}
    </div>
  );
};

export default Admissions;
